import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { ColumnDef } from '@tanstack/react-table';
import { ExternalLink, FileText, KeyRound, Plus, Trash2 } from 'lucide-react';
import type { Entity, EntityCreate, EntityDrift, EntityHeartbeatList, EntityList, EntityStatus, EntityTokenIssued, EntityUpdate } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { DataTable } from '../../components/DataTable';
import { Badge, ConfirmDialog, Field, Modal, PageHeader, SearchableSelect, cn } from '../../components/ui';
import { parseEntitySearch, type EntitySearch } from './search';
import { GrantsEditor } from '../registry/GrantsEditor';

const STATUSES: EntityStatus[] = ['planned', 'active', 'paused', 'closed'];
const STATUS_TONE: Record<EntityStatus, 'neutral' | 'brand' | 'warn' | 'danger' | 'info'> = { planned: 'info', active: 'brand', paused: 'warn', closed: 'neutral' };
const SEVERE: EntityDrift[] = ['never_seen', 'no_heartbeat', 'stopped', 'version_behind'];

type FormState = { slug: string; name_en: string; name_ar: string; base_url: string; status: EntityStatus; go_live_date: string; contact_name: string; contact_email: string; contact_phone: string; desired_version: string; desired_modules: string; residency_note: string; notes: string };
const EMPTY: FormState = { slug: '', name_en: '', name_ar: '', base_url: '', status: 'planned', go_live_date: '', contact_name: '', contact_email: '', contact_phone: '', desired_version: '', desired_modules: '', residency_note: '', notes: '' };

export function DriftBadges({ drift }: { drift: EntityDrift[] }) {
  const { t } = useTranslation();
  if (drift.length === 0) return <Badge tone="brand">{t('directory.healthy')}</Badge>;
  return (
    <span className="flex flex-wrap gap-1">
      {drift.map((d) => <Badge key={d} tone={SEVERE.includes(d) ? 'danger' : 'warn'}>{t(`directory.drift.${d}`)}</Badge>)}
    </span>
  );
}

/** Entity directory (central): what each entity should run against what it reports, and its service token. */
export function EntitiesPage() {
  const { t, i18n } = useTranslation();
  const { can, canAction } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = parseEntitySearch(useSearch({ strict: false }) as Record<string, unknown>);
  const [editing, setEditing] = useState<Entity | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [detail, setDetail] = useState<Entity | null>(null);
  const [issued, setIssued] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'token' | 'revoke' | 'delete'; entity: Entity } | null>(null);
  const [grantsFor, setGrantsFor] = useState<Entity | null>(null);

  const setSearch = (patch: Partial<EntitySearch>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    void navigate({ to: '/entities', search: next as EntitySearch });
  };

  const list = useQuery({
    queryKey: ['entities', search],
    queryFn: () => api.get<EntityList>(`/api/entities${qs({ q: search.q, status: search.status, attention: search.attention === '1' ? 'true' : undefined, sort: search.sort, page: search.page, page_size: 25 })}`),
    refetchInterval: 60_000,
  });
  const beats = useQuery({ queryKey: ['entities', 'heartbeats', detail?.id], queryFn: () => api.get<EntityHeartbeatList>(`/api/entities/${detail!.id}/heartbeats`), enabled: Boolean(detail) });
  const done = (message: string) => {
    toast.success(message);
    void qc.invalidateQueries({ queryKey: ['entities'] });
  };
  const fail = (e: unknown) => toast.error(describeError(e, t));

  const save = useMutation({
    mutationFn: () => {
      const common = {
        name_en: form.name_en,
        name_ar: form.name_ar,
        base_url: form.base_url || null,
        status: form.status,
        go_live_date: form.go_live_date || null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        desired_version: form.desired_version || null,
        desired_modules: form.desired_modules.split(',').map((m) => m.trim()).filter(Boolean),
        residency_note: form.residency_note || null,
        notes: form.notes || null,
      };
      return editing === 'new' ? api.post<Entity>('/api/entities', { slug: form.slug, ...common } satisfies EntityCreate) : api.patch<Entity>(`/api/entities/${(editing as Entity).id}`, common satisfies EntityUpdate);
    },
    onSuccess: () => {
      done(t('directory.saved'));
      setEditing(null);
    },
    onError: fail,
  });
  const issue = useMutation({
    mutationFn: (id: string) => api.post<EntityTokenIssued>(`/api/entities/${id}/token`),
    onSuccess: (res) => {
      setIssued(res.token);
      void qc.invalidateQueries({ queryKey: ['entities'] });
    },
    onError: fail,
  });
  const revoke = useMutation({ mutationFn: (id: string) => api.delete(`/api/entities/${id}/token`), onSuccess: () => done(t('directory.tokenRevoked')), onError: fail });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/entities/${id}`), onSuccess: () => done(t('directory.deleted')), onError: fail });

  const openNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };
  const openEdit = (e: Entity) => {
    setForm({ slug: e.slug, name_en: e.name_en, name_ar: e.name_ar, base_url: e.base_url ?? '', status: e.status, go_live_date: e.go_live_date ?? '', contact_name: e.contact_name ?? '', contact_email: e.contact_email ?? '', contact_phone: e.contact_phone ?? '', desired_version: e.desired_version ?? '', desired_modules: e.desired_modules.join(', '), residency_note: e.residency_note ?? '', notes: e.notes ?? '' });
    setEditing(e);
  };
  const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : t('directory.never'));

  const columns = useMemo<ColumnDef<Entity, unknown>[]>(
    () => [
      { id: 'name_en', header: t('directory.entity'), accessorFn: (e) => e.name_en, cell: ({ row }) => (<div><div className="font-medium" dir="auto">{pick(i18n.language, row.original.name_en, row.original.name_ar)}</div><code className="text-xs text-neutral-500">{row.original.slug}</code></div>) },
      { id: 'status', header: t('directory.status'), accessorFn: (e) => e.status, cell: ({ row }) => <Badge tone={STATUS_TONE[row.original.status]}>{t(`directory.statuses.${row.original.status}`)}</Badge> },
      { id: 'version', header: t('directory.version'), enableSorting: false, accessorFn: (e) => e.actual?.version ?? '', cell: ({ row }) => (<div className="text-sm"><div>{row.original.actual?.version ?? '—'}</div>{row.original.desired_version && <div className="text-xs text-neutral-500">{t('directory.wanted', { v: row.original.desired_version })}</div>}</div>) },
      { id: 'last_heartbeat_at', header: t('directory.lastHeartbeat'), accessorFn: (e) => e.last_heartbeat_at ?? '', cell: ({ row }) => <span className="text-sm">{when(row.original.last_heartbeat_at)}</span> },
      { id: 'health', header: t('directory.health'), enableSorting: false, accessorFn: (e) => e.drift.length, cell: ({ row }) => (row.original.status === 'active' ? <DriftBadges drift={row.original.drift} /> : <span className="text-xs text-neutral-400">—</span>) },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1" onClick={(ev) => ev.stopPropagation()}>
            {row.original.base_url && <a className="btn-secondary px-2 py-1" href={row.original.base_url} target="_blank" rel="noreferrer" title={t('directory.open')}><ExternalLink size={14} /></a>}
            {canAction('entities.manage_tokens') && <button type="button" className="btn-secondary px-2 py-1" title={t('directory.issueToken')} onClick={() => setConfirm({ kind: 'token', entity: row.original })}><KeyRound size={14} /></button>}
            {can('entities', 'can_delete') && <button type="button" className="btn-secondary px-2 py-1" title={t('app.delete')} onClick={() => setConfirm({ kind: 'delete', entity: row.original })}><Trash2 size={14} /></button>}
          </div>
        ),
      },
    ],
    [t, i18n.language, can, canAction],
  );

  const meta = list.data?.meta;
  return (
    <div>
      <PageHeader title={t('directory.pageTitle')} subtitle={t('directory.subtitle')} actions={can('entities', 'can_create') && <button type="button" className="btn-primary" onClick={openNew}><Plus size={16} /> {t('directory.new')}</button>} />
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isLoading}
        emptyText={t('directory.none')}
        onRowClick={(e) => setDetail(e)}
        sort={{ value: search.sort ?? '', onChange: (sort) => setSearch({ sort: sort || undefined, page: undefined }) }}
        page={meta ? { page: meta.page, page_size: meta.page_size, total: meta.total, onChange: (page) => setSearch({ page: page > 1 ? page : undefined }) } : undefined}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <input className="input w-56" placeholder={t('app.search')} defaultValue={search.q ?? ''} onKeyDown={(ev) => ev.key === 'Enter' && setSearch({ q: (ev.target as HTMLInputElement).value || undefined, page: undefined })} />
            <div className="w-44"><SearchableSelect value={search.status ?? ''} onChange={(v) => setSearch({ status: (v as EntityStatus) || undefined, page: undefined })} options={[{ value: '', label: t('directory.anyStatus') }, ...STATUSES.map((s) => ({ value: s, label: t(`directory.statuses.${s}`) }))]} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={search.attention === '1'} onChange={(ev) => setSearch({ attention: ev.target.checked ? '1' : undefined, page: undefined })} /> {t('directory.attentionOnly')}</label>
          </div>
        }
      />

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('directory.new') : t('directory.edit')} wide footer={<><button type="button" className="btn-secondary" onClick={() => setEditing(null)}>{t('app.cancel')}</button><button type="button" className="btn-primary" disabled={save.isPending || !form.name_en || !form.name_ar || (editing === 'new' && !form.slug)} onClick={() => save.mutate()}>{t('app.save')}</button></>}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('directory.slug')} required hint={t('directory.slugHint')}><input className="input" dir="ltr" disabled={editing !== 'new'} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })} /></Field>
          <Field label={t('directory.status')}><SearchableSelect value={form.status} onChange={(v) => setForm({ ...form, status: v as EntityStatus })} options={STATUSES.map((s) => ({ value: s, label: t(`directory.statuses.${s}`) }))} /></Field>
          <Field label={t('directory.nameEn')} required><input className="input" dir="ltr" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} /></Field>
          <Field label={t('directory.nameAr')} required><input className="input" dir="rtl" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></Field>
          <Field label={t('directory.baseUrl')}><input className="input" dir="ltr" placeholder="https://" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} /></Field>
          <Field label={t('directory.goLive')}><input className="input" type="date" value={form.go_live_date} onChange={(e) => setForm({ ...form, go_live_date: e.target.value })} /></Field>
          <Field label={t('directory.desiredVersion')} hint={t('directory.desiredVersionHint')}><input className="input" dir="ltr" value={form.desired_version} onChange={(e) => setForm({ ...form, desired_version: e.target.value })} /></Field>
          <Field label={t('directory.desiredModules')} hint={t('directory.desiredModulesHint')}><input className="input" dir="ltr" value={form.desired_modules} onChange={(e) => setForm({ ...form, desired_modules: e.target.value })} /></Field>
          <Field label={t('directory.contactName')}><input className="input" dir="auto" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
          <Field label={t('directory.contactEmail')}><input className="input" dir="ltr" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
          <Field label={t('directory.contactPhone')}><input className="input" dir="ltr" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
          <Field label={t('directory.residency')}><input className="input" dir="auto" value={form.residency_note} onChange={(e) => setForm({ ...form, residency_note: e.target.value })} /></Field>
          <div className="sm:col-span-2"><Field label={t('directory.notes')}><textarea className="input min-h-20" dir="auto" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>
        </div>
      </Modal>

      <Modal open={detail !== null} onClose={() => setDetail(null)} title={detail ? pick(i18n.language, detail.name_en, detail.name_ar) : ''} wide footer={<>{detail && can('entities', 'can_edit') && <button type="button" className="btn-secondary" onClick={() => { openEdit(detail); setDetail(null); }}>{t('app.edit')}</button>}<button type="button" className="btn-primary" onClick={() => setDetail(null)}>{t('app.close')}</button></>}>
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2"><code>{detail.slug}</code><Badge tone={STATUS_TONE[detail.status]}>{t(`directory.statuses.${detail.status}`)}</Badge>{detail.status === 'active' && <DriftBadges drift={detail.drift} />}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Compare label={t('directory.version')} wanted={detail.desired_version} actual={detail.actual?.version ?? null} />
              <Compare label={t('directory.modules')} wanted={detail.desired_modules.join(', ') || null} actual={detail.actual ? detail.actual.enabled_modules.filter((m) => m !== 'core').join(', ') : null} />
              <Info label={t('directory.seedPack')} value={detail.actual?.seed_pack_version} />
              <Info label={t('directory.migrations')} value={detail.actual ? String(detail.actual.migrations_applied) : null} />
              <Info label={t('directory.users')} value={detail.actual ? String(detail.actual.user_count) : null} />
              <Info label={t('directory.lastBackup')} value={detail.actual ? when(detail.actual.last_backup_at) : null} />
              <Info label={t('directory.lastHeartbeat')} value={when(detail.last_heartbeat_at)} />
              <Info label={t('directory.token')} value={detail.has_token ? t('directory.tokenSince', { when: when(detail.token_issued_at) }) : t('directory.noToken')} />
              <Info label={t('directory.contactName')} value={[detail.contact_name, detail.contact_email, detail.contact_phone].filter(Boolean).join(' · ') || null} />
              <Info label={t('directory.residency')} value={detail.residency_note} />
            </div>
            {detail.notes && <p className="whitespace-pre-line rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800" dir="auto">{detail.notes}</p>}
            <div>
              <div className="label">{t('directory.history')}</div>
              {(beats.data?.items ?? []).length === 0 ? <p className="text-neutral-500">{t('directory.noHistory')}</p> : (
                <ul className="max-h-48 divide-y divide-neutral-100 overflow-auto dark:divide-neutral-800">
                  {beats.data!.items.map((b) => <li key={b.id} className="flex items-center justify-between gap-3 py-1"><span>{when(b.received_at)}</span><span className="text-neutral-500">{b.payload.version} · {b.payload.enabled_modules.length} · {b.payload.user_count}</span></li>)}
                </ul>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary" onClick={() => void api.download(`/api/entities/${detail.id}/document?locale=${i18n.language === 'ar' ? 'ar' : 'en'}`)}><FileText size={16} /> {t('directory.downloadDocument')}</button>
              {can('registry') && <button type="button" className="btn-secondary" onClick={() => setGrantsFor(detail)}>{t('registry.grantsButton')}</button>}
              {canAction('entities.manage_tokens') && detail.has_token && <button type="button" className="btn-danger" onClick={() => { setConfirm({ kind: 'revoke', entity: detail }); setDetail(null); }}>{t('directory.revokeToken')}</button>}
            </div>
          </div>
        )}
      </Modal>

      {grantsFor && <GrantsEditor entityId={grantsFor.id} open onClose={() => setGrantsFor(null)} />}

      <Modal open={issued !== null} onClose={() => setIssued(null)} title={t('directory.tokenIssued')} footer={<button type="button" className="btn-primary" onClick={() => setIssued(null)}>{t('app.close')}</button>}>
        <p className="mb-2 text-sm text-neutral-600 dark:text-neutral-300">{t('directory.tokenOnce')}</p>
        <div className="flex items-center gap-2"><code className="min-w-0 flex-1 break-all rounded-lg bg-neutral-100 p-2 text-xs dark:bg-neutral-800">{issued}</code><button type="button" className="btn-secondary" onClick={() => { void navigator.clipboard.writeText(issued ?? ''); toast.success(t('app.copied')); }}>{t('app.copy')}</button></div>
        <p className="mt-2 text-xs text-neutral-500">{t('directory.tokenWhere')}</p>
      </Modal>

      <ConfirmDialog
        open={confirm !== null}
        danger={confirm?.kind !== 'token'}
        title={confirm ? t(`directory.confirm.${confirm.kind}`) : ''}
        body={confirm ? `${pick(i18n.language, confirm.entity.name_en, confirm.entity.name_ar)} · ${t(`directory.confirmBody.${confirm.kind}`)}` : undefined}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'token') issue.mutate(confirm.entity.id);
          if (confirm.kind === 'revoke') revoke.mutate(confirm.entity.id);
          if (confirm.kind === 'delete') remove.mutate(confirm.entity.id);
          setConfirm(null);
        }}
      />
    </div>
  );
}

function Info({ label, value }: { label: string; value: string | null | undefined }) {
  return (<div><div className="label">{label}</div><div dir="auto">{value || '—'}</div></div>);
}

/** Desired against actual, side by side; a mismatch is highlighted. */
function Compare({ label, wanted, actual }: { label: string; wanted: string | null; actual: string | null }) {
  const { t } = useTranslation();
  const differs = Boolean(wanted) && Boolean(actual) && wanted !== actual;
  return (
    <div>
      <div className="label">{label}</div>
      <div className={cn(differs && 'font-medium text-amber-600')} dir="ltr">{actual ?? '—'}</div>
      {wanted && <div className="text-xs text-neutral-500" dir="ltr">{t('directory.wanted', { v: wanted })}</div>}
    </div>
  );
}
